import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Cross-sell popup shown right after a product is added to the cart.
 * Added independently of the theme's own cart components: it only listens
 * to the standard "shopify:cart:lines-update" event and reuses the same
 * event class to add its own offer, so cart-icon / cart-drawer stay in sync
 * without any of their code being touched.
 */
class CartUpsellPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.addButton = this.querySelector('[data-upsell-add]');
    this.imageEl = this.querySelector('[data-upsell-image]');
    this.nameEl = this.querySelector('[data-upsell-name]');
    this.priceEl = this.querySelector('[data-upsell-price]');

    const dataScript = document.getElementById('zyrox-upsell-data');
    try {
      this.data = dataScript ? JSON.parse(dataScript.textContent) : { offers: {}, suggestByVariant: {} };
    } catch (error) {
      console.warn('[cart-upsell] Could not parse offer data:', error);
      this.data = { offers: {}, suggestByVariant: {} };
    }

    this.querySelectorAll('[data-upsell-close]').forEach((button) => {
      button.addEventListener('click', () => this.dialog.close());
    });

    this._onCartUpdate = this.onCartUpdate.bind(this);
    document.addEventListener('shopify:cart:lines-update', this._onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:cart:lines-update', this._onCartUpdate);
  }

  onCartUpdate(event) {
    if (event.action !== 'add' || event.context === 'cart-upsell') return;

    const rawMerchandiseId = String(event.lines?.[0]?.merchandiseId || '');
    // merchandiseId can arrive as a plain numeric id ("123") or a full GID
    // ("gid://shopify/ProductVariant/123"), depending on where the add came
    // from. Normalize to the trailing digits so lookups always match.
    const addedVariantId = rawMerchandiseId.match(/(\d+)$/)?.[1] || '';
    if (!addedVariantId) return;

    const offerHandle = this.data.suggestByVariant?.[addedVariantId];
    if (!offerHandle) return;

    const offer = this.data.offers?.[offerHandle];
    if (!offer) return;

    event.promise
      ?.then(({ detail, cart }) => {
        if (detail?.didError) return;

        const cartItems = cart?.items || detail?.items || [];
        const alreadyInCart = cartItems.some(
          (item) => String(item.variant_id ?? item.id) === String(offer.variantId)
        );
        if (alreadyInCart) return;

        this.showOffer(offer);
      })
      .catch(() => {
        // Add-to-cart failed elsewhere; nothing to offer.
      });
  }

  showOffer(offer) {
    this.imageEl.src = offer.image;
    this.imageEl.alt = offer.title;
    this.nameEl.textContent = offer.title;
    this.priceEl.textContent = offer.price;
    this.addButton.disabled = false;
    this.addButton.onclick = () => this.addOffer(offer.variantId);

    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
  }

  addOffer(variantId) {
    this.addButton.disabled = true;

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart-upsell',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          throw new Error(response.message || 'Add to cart failed');
        }

        return fetch(`${Theme.routes.cart_url}.json`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        }).then((response) => response.json());
      })
      .then((ajaxCart) => {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            items: ajaxCart.items,
            source: 'cart-upsell',
            itemCount: ajaxCart.item_count,
            didError: false,
          },
        });
        this.dialog.close();
      })
      .catch((error) => {
        console.warn('[cart-upsell] Could not add offer to cart:', error);
        deferred.reject(error);
        this.addButton.disabled = false;
      });
  }
}

if (!customElements.get('cart-upsell-popup')) {
  customElements.define('cart-upsell-popup', CartUpsellPopup);
}
